<template>
  <LayoutApplicationStyle>
    <div class="pp-wrap">
      <PostPurchaseSteps :current="1" />

      <div class="pp-card">
        <aside class="pp-side">
          <div class="pp-tag">{{ copy.tag }}</div>
          <h3>{{ copy.sideTitle }}</h3>
          <div v-for="(point, i) in copy.sidePoints" :key="i" class="pp-li">
            <span class="pp-n">{{ i + 1 }}</span>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="point"></div>
          </div>
        </aside>

        <div class="pp-main">
          <h2>{{ question.title }}</h2>
          <button
            v-for="option in question.options"
            :key="option.value"
            type="button"
            class="pp-opt"
            :class="{ 'pp-sel': answers[pane] === option.value }"
            @click="pick(option.value)"
          >
            <span class="pp-ico">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <svg
                viewBox="0 0 24 24"
                width="26"
                height="26"
                fill="none"
                stroke="currentColor"
                stroke-width="1.9"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
                v-html="option.icon"
              ></svg>
            </span>
            <span class="pp-body">
              <span class="pp-ans">{{ option.answer }}</span>
              <span class="pp-txt">{{ option.text }}</span>
            </span>
            <span class="pp-chk"><CheckIcon /></span>
          </button>
          <button v-if="pane" type="button" class="pp-link" @click="reset">
            {{ copy.back }}
          </button>
        </div>
      </div>
    </div>
  </LayoutApplicationStyle>
</template>

<script>
import { computed, onMounted, reactive } from '@nuxtjs/composition-api';
import LayoutApplicationStyle from '../LayoutApplicationStyle.vue';
import PostPurchaseSteps from '../PostPurchaseSteps.vue';
import CheckIcon from '../CheckIcon.vue';
import { Handoff } from '../../constants/PostPurchase';
import { SupervisionLevel } from '@/constants/Experiments';
import useRegistration, {
  RegistrationStateNames as R,
} from '../../hooks/useRegistration';
import track from '../../utils/postPurchaseTrack';

/**
 * "How much do you want Bambee to take off your plate?" Two questions that pick a
 * coverage level: "Nothing" is Observation; "All of it" asks who runs the work,
 * Management or Supervision. Coming back here shows the answers already given.
 */
export default {
  name: 'HandoffPage',
  components: { CheckIcon, LayoutApplicationStyle, PostPurchaseSteps },
  setup(_, { emit }) {
    const { state, updateRegistration } = useRegistration();
    const level = state[R.SUPERVISION_LEVEL];
    // Keyed by question index. An object, not an array: Vue 2 cannot observe
    // assignment to an array index.
    const answers = reactive({
      0: level && (level === SupervisionLevel.OBSERVATION ? 'none' : 'all'),
      1: level !== SupervisionLevel.OBSERVATION ? level : null,
    });
    const pane = computed(() => (answers[0] === 'all' ? 1 : 0));
    const question = computed(() => Handoff.questions[pane.value]);

    const choose = (next) =>
      emit('submit', {
        [R.SUPERVISION_LEVEL]: next,
        // A new level invalidates a plan confirmed under the old one.
        [R.SUPERVISION_PLAN]:
          next === state[R.SUPERVISION_LEVEL] ? state[R.SUPERVISION_PLAN] : null,
      });

    const pick = (value) => {
      answers[pane.value] = value;
      track(state, 'handoff-answered', {
        question: pane.value + 1,
        answer: value,
      });
      if (value === 'none') {
        choose(SupervisionLevel.OBSERVATION);
      } else if (value !== 'all') {
        choose(value);
      }
    };

    const reset = () => {
      answers[0] = null;
      answers[1] = null;
      updateRegistration({ [R.SUPERVISION_LEVEL]: '', [R.SUPERVISION_PLAN]: null });
      track(state, 'handoff-reset');
    };

    onMounted(() => track(state, 'handoff-viewed'));

    return { copy: Handoff, answers, pane, question, pick, reset };
  },
};
</script>
